import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Mail, ArrowLeft, Check, X, Loader2, Settings, Eye, EyeOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailSettings = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    email_address: '',
    password: '',
    provider: 'microsoft365',
    display_name: ''
  });

  const providers = [
    { value: 'microsoft365', label: 'Microsoft 365 / Outlook', description: 'Office 365, Outlook.com' },
    { value: 'gmail', label: 'Gmail / Google Workspace', description: 'Gmail, Google Workspace' },
    { value: 'outlook', label: 'Outlook.com (Personal)', description: 'Personal Outlook/Hotmail' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/email/settings?token=${token}`);
      setSettings(response.data);
      if (response.data.configured) {
        setFormData({
          email_address: response.data.email_address || '',
          password: '',
          provider: response.data.provider || 'microsoft365',
          display_name: response.data.display_name || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch email settings', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!formData.email_address || !formData.password) {
      toast.error('Please enter email address and password');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/email/settings?token=${token}`, formData);
      if (response.data.success) {
        toast.success('Email settings saved!');
        
        // Check connection test result
        if (response.data.connection_test?.success) {
          toast.success('Connection test passed!');
        } else if (response.data.connection_test?.error) {
          toast.error(`Connection test failed: ${response.data.connection_test.error}`);
        }
        
        await fetchSettings();
      }
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/email/test-connection?token=${token}`);
      if (response.data.success) {
        toast.success('Connection successful! Email is working.');
      } else {
        toast.error(response.data.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Connection test failed', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="text-blue-500" />
            Email Settings
          </h1>
          <p className="text-slate-500 text-sm">Configure email for sending and receiving</p>
        </div>
      </div>

      {/* Status Card */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            Connection Status
            <Badge variant={settings?.configured ? 'default' : 'secondary'} 
                   className={settings?.configured ? 'bg-green-500' : ''}>
              {settings?.configured ? (
                <><Check size={14} className="mr-1" /> Configured</>
              ) : (
                <><X size={14} className="mr-1" /> Not Configured</>
              )}
            </Badge>
          </CardTitle>
        </CardHeader>
        {settings?.configured && (
          <CardContent>
            <div className="space-y-1 text-sm">
              <p className="text-slate-600">
                <strong>Email:</strong> {settings.email_address}
              </p>
              <p className="text-slate-600">
                <strong>Display Name:</strong> {settings.display_name || 'Not set'}
              </p>
              <p className="text-slate-600">
                <strong>Server:</strong> {settings.smtp_server}
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Configuration Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings size={18} />
            Email Configuration
          </CardTitle>
          <CardDescription>
            Enter your email credentials. For Microsoft 365, use your regular email and password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="provider">Email Provider</Label>
            <Select
              value={formData.provider}
              onValueChange={(value) => setFormData({ ...formData, provider: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    <div>
                      <span className="font-medium">{p.label}</span>
                      <span className="text-slate-500 text-xs ml-2">{p.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="email_address">Email Address</Label>
            <Input
              id="email_address"
              type="email"
              value={formData.email_address}
              onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
              placeholder="crm@yourcompany.com"
            />
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={settings?.configured ? "Enter new password to update" : "Enter password"}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              For Microsoft 365 with MFA, you may need to create an App Password in your Microsoft account security settings.
            </p>
          </div>

          <div>
            <Label htmlFor="display_name">Display Name (Optional)</Label>
            <Input
              id="display_name"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              placeholder="Fakhruddin Properties CRM"
            />
            <p className="text-xs text-slate-500 mt-1">
              Name shown in the "From" field when sending emails
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Settings size={16} className="mr-2" />}
              Save Settings
            </Button>
            {settings?.configured && (
              <Button variant="outline" onClick={testConnection} disabled={testing}>
                {testing ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                Test Connection
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Need Help?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-600">
          <div>
            <p className="font-medium text-slate-800">Microsoft 365 / Office 365:</p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>Use your regular Microsoft 365 email and password</li>
              <li>If you have MFA enabled, create an App Password at <a href="https://account.microsoft.com/security" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">account.microsoft.com/security</a></li>
              <li>Make sure SMTP is enabled for your mailbox (admin may need to enable it)</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-slate-800">Gmail / Google Workspace:</p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>Enable "Less secure app access" or create an App Password</li>
              <li>Go to Google Account → Security → App passwords</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettings;
